import { Box, Stack } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { FC, useState } from 'react';

import { useSetPersonEventsAccess } from '@/api/events-access/mutations';
import { _getEventsAccess } from '@/api/events-access/queries';
import { EventsAccessPerson } from '@/api/events-access/types';
import { Permission } from '@/api/permissions';
import { Button, Input, theme, Typography } from '@/shared/design-system';
import { useCan } from '@/shared/modules/permissions';
import { useProfileLookup } from '@/shared/modules/user-lookup';

const NO_ACCOUNT_MESSAGE = 'There is no Gamma account for this email address.';

const PersonRow: FC<{ person: EventsAccessPerson; onRemove: () => void; disabled: boolean }> = ({
  person,
  onRemove,
  disabled,
}) => (
  <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2}>
    <Box>
      <Typography variant="body3" color={theme.colors.text.primary}>
        {person.name ?? person.email ?? 'Unknown user'}
      </Typography>
      <Typography variant="body4" color={theme.colors.text.secondary}>
        {person.email ?? 'This grant has outlived its user account.'}
      </Typography>
    </Box>

    <Button
      variant="outline"
      onClick={onRemove}
      disabled={disabled}
      aria-label={`Remove ${person.email ?? person.userId}`}
    >
      Remove
    </Button>
  </Stack>
);

/**
 * Decides who may manage their own events, from the Gamma Dashboard — the panel that replaced the
 * Events Creation and Manage Center when event management moved to the Managing Events subtab.
 *
 * Two ways in, only one of which is administered here. Everyone with an active introducer record
 * hosts events automatically — evaluated from live status on every permission resolve, so it
 * follows people gaining and losing that record rather than snapshotting them, and there is
 * nothing to switch on. This panel appoints the individuals who are not introducers.
 *
 * Everyone granted here manages only the events they host. Gamma's own events stay with
 * `ViewAndManageGammaEvents`, which this panel cannot give away.
 *
 * The email field resolves against every Gamma Prime profile, deliberately not the Gamma team
 * dashboard the role-member picker uses — the people granted here are typically introducers or
 * entity users, who never appear in that list.
 */
export const EventsAccessTab: FC = () => {
  const canManageAccess = useCan(Permission.GammaManageEventsAccess);
  const { data } = useQuery(_getEventsAccess());
  const setPersonAccess = useSetPersonEventsAccess();

  const [email, setEmail] = useState('');
  const { matchedUser, emailIsValid, lookupPending } = useProfileLookup(email, email.length > 0);

  const people = data?.people ?? [];
  const showNoAccount = emailIsValid && !lookupPending && !matchedUser && email.length > 0;

  const grantToPerson = async () => {
    if (!matchedUser) return;

    await setPersonAccess.mutateAsync({ userId: matchedUser.id, granted: true });
    setEmail('');
  };

  return (
    <Stack gap={4}>
      <Box>
        <Typography variant="body2" fontWeight={600} color={theme.colors.text.primary}>
          Introducers
        </Typography>

        <Typography variant="body4" color={theme.colors.text.secondary}>
          Everyone with an active introducer record can host their own events. Access follows their
          status, so it starts and stops with the record and is not granted here.
        </Typography>
      </Box>

      <Box>
        <Typography variant="body2" fontWeight={600} color={theme.colors.text.primary}>
          By person
        </Typography>

        <Input
          id="events-access-email"
          name="email"
          type="email"
          label="Email"
          // Paired with `label` deliberately: the design system's Field.Label binds through a
          // Field.Root context this panel does not provide, so the input needs its own name.
          aria-label="Email"
          autoComplete="off"
          spellCheck={false}
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          disabled={!canManageAccess}
          placeholder="host@example.com"
        />

        {showNoAccount && (
          <Typography variant="body4" color={theme.colors.text.secondary}>
            {NO_ACCOUNT_MESSAGE}
          </Typography>
        )}

        <Button
          onClick={grantToPerson}
          disabled={!canManageAccess || !matchedUser || setPersonAccess.isPending}
        >
          Grant access
        </Button>
      </Box>

      <Box>
        <Typography variant="body2" fontWeight={600} color={theme.colors.text.primary}>
          People with access
        </Typography>

        {people.length === 0 ? (
          <Typography variant="body4" color={theme.colors.text.secondary}>
            Nobody has been granted access individually.
          </Typography>
        ) : (
          <Stack gap={2}>
            {people.map((person) => (
              <PersonRow
                key={person.userId}
                person={person}
                disabled={!canManageAccess || setPersonAccess.isPending}
                onRemove={() =>
                  setPersonAccess.mutateAsync({ userId: person.userId, granted: false })
                }
              />
            ))}
          </Stack>
        )}
      </Box>
    </Stack>
  );
};

export default EventsAccessTab;
